// Read & write PDF outline (bookmarks). We read via pdfjs (which has a
// convenient getOutline()), then represent changes as a flat list of edits:
// either keep an entry with an updated title, or drop it. On export, we
// strip and re-write the outline tree using pdf-lib's low-level context.

import { loadPdfJsDocument } from './pdfjs.js'
import { PDFName, PDFDict, PDFArray, PDFString, PDFRef } from 'pdf-lib'

export async function readOutline(doc) {
  const pdf = await loadPdfJsDocument(doc.id, doc.bytes)
  const raw = await pdf.getOutline()
  if (!raw || raw.length === 0) return []
  // Flatten the (potentially nested) tree into a list with depth.
  const out = []
  function walk(items, depth) {
    for (const it of items) {
      out.push({
        title: it.title || '',
        depth,
        kept: true,
      })
      if (it.items && it.items.length) walk(it.items, depth + 1)
    }
  }
  walk(raw, 0)
  return out
}

// Remove the /Outlines entry from a pdf-lib document's catalog. This is the
// safest cross-PDF way to wipe bookmarks.
export function stripOutline(pdf) {
  try {
    const catalog = pdf.catalog
    catalog.delete(PDFName.of('Outlines'))
    catalog.delete(PDFName.of('PageMode'))
  } catch (_) {
    // best-effort
  }
}

// Build a flat outline (no nesting) from a list of { title, pageIndex } and
// attach it to the supplied pdf-lib document.
export function writeFlatOutline(pdf, entries) {
  if (!entries || entries.length === 0) return
  try {
    const ctx = pdf.context
    const pages = pdf.getPages()

    const outlinesRef = ctx.nextRef()
    const itemRefs = entries.map(() => ctx.nextRef())

    entries.forEach((entry, i) => {
      const page = pages[Math.max(0, Math.min(pages.length - 1, entry.pageIndex || 0))]
      const dest = PDFArray.withContext(ctx)
      dest.push(page.ref)
      dest.push(PDFName.of('Fit'))

      const itemDict = ctx.obj({})
      itemDict.set(PDFName.of('Title'), PDFString.of(entry.title || `Section ${i + 1}`))
      itemDict.set(PDFName.of('Parent'), outlinesRef)
      itemDict.set(PDFName.of('Dest'), dest)
      if (i > 0) itemDict.set(PDFName.of('Prev'), itemRefs[i - 1])
      if (i < entries.length - 1) itemDict.set(PDFName.of('Next'), itemRefs[i + 1])
      ctx.assign(itemRefs[i], itemDict)
    })

    const outlinesDict = ctx.obj({})
    outlinesDict.set(PDFName.of('Type'), PDFName.of('Outlines'))
    outlinesDict.set(PDFName.of('First'), itemRefs[0])
    outlinesDict.set(PDFName.of('Last'), itemRefs[itemRefs.length - 1])
    outlinesDict.set(PDFName.of('Count'), ctx.obj(entries.length))
    ctx.assign(outlinesRef, outlinesDict)

    pdf.catalog.set(PDFName.of('Outlines'), outlinesRef)
  } catch (err) {
    console.warn('writeFlatOutline failed', err)
  }
}
